import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Container from './Container.jsx'
import Logo from './Logo.jsx'
import SocialIcon from './SocialIcon.jsx'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

const columns = [
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/about' },
      { label: 'Portfolio', to: '/portfolio' },
      { label: 'Careers', to: '/careers' },
      { label: 'Contact Us', to: '/contact' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'FAQ', to: '/faqs' },
      { label: 'How We Work', to: '/how-we-work' },
      { label: 'Services', to: '/services' },
      { label: 'Support', to: '/contact' },
    ],
  },
  {
    title: 'Legal Polices',
    links: [
      { label: 'Terms & Conditions', to: '/terms' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'GDPR Compliance', to: '/gdpr' },
      { label: 'FAQs', to: '/faqs' },
    ],
  },
]

/**
 * Social links come from the portal's Social Media tab. Until a super admin
 * adds one, the row renders nothing at all — no placeholder icons.
 */
function useSocialLinks() {
  const [links, setLinks] = useState([])

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/manage_social_links.php?action=list`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && data.success) setLinks(data.links || [])
      })
      .catch(() => {
        /* the footer must render with or without the API */
      })
    return () => { cancelled = true }
  }, [])

  return links
}

export default function Footer() {
  const socials = useSocialLinks()

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink-900">
      {/* colorful gradient blob (bottom-right) */}
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-gradient-to-tr from-brand/40 via-sky-500/30 to-accent/40 blur-[90px]" />

      <Container className="relative py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-5 max-w-xs text-sm text-slate-400">
              Trusted IT &amp; web solutions for startups &amp; enterprises. We design, build, secure and grow
              your digital presence, end to end.
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3">
                {socials.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.platform}
                    aria-label={s.platform}
                    className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-400 transition hover:border-brand-400/40 hover:text-brand-300"
                  >
                    <SocialIcon name={s.platform} url={s.url} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-1" />

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="text-sm font-semibold text-white">{col.title}</h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-400 transition hover:text-white">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 text-center">
          <p className="text-sm text-slate-400">
            Copyright © {new Date().getFullYear()} Spectranox Technologies Pvt Ltd. All rights reserved.
          </p>
        </div>
      </Container>
    </footer>
  )
}
